import React from 'react';
import { Link, useParams } from 'react-router-dom';
import { ArrowLeft, Tag as TagIcon, Download } from 'lucide-react';
import { useWorkspace } from '../contexts/WorkspaceContext';
export function TagDetail() {
  const {
    tagName
  } = useParams<{
    tagName: string;
  }>();
  const {
    tags,
    getSubmissionsByTag
  } = useWorkspace();
  const tag = tags.find(t => t.name === tagName);
  const submissions = getSubmissionsByTag(tagName || '');
  const tagColors: Record<string, {
    bg: string;
    text: string;
    border: string;
  }> = {
    blue: {
      bg: 'bg-blue-50',
      text: 'text-blue-700',
      border: 'border-blue-200'
    },
    green: {
      bg: 'bg-green-50',
      text: 'text-green-700',
      border: 'border-green-200'
    },
    purple: {
      bg: 'bg-purple-50',
      text: 'text-purple-700',
      border: 'border-purple-200'
    },
    orange: {
      bg: 'bg-orange-50',
      text: 'text-orange-700',
      border: 'border-orange-200'
    },
    red: {
      bg: 'bg-red-50',
      text: 'text-red-700',
      border: 'border-red-200'
    },
    yellow: {
      bg: 'bg-yellow-50',
      text: 'text-yellow-700',
      border: 'border-yellow-200'
    },
    pink: {
      bg: 'bg-pink-50',
      text: 'text-pink-700',
      border: 'border-pink-200'
    },
    gray: {
      bg: 'bg-gray-50',
      text: 'text-gray-700',
      border: 'border-gray-200'
    }
  };
  const color = tag ? tagColors[tag.color] || tagColors.blue : tagColors.blue;
  return <div className="space-y-6" data-id="element-2020">
      <div className="flex items-center justify-between" data-id="element-2021">
        <div className="flex items-center gap-4" data-id="element-2022">
          <Link to="/tags" className="p-2 hover:bg-gray-100 rounded-full text-gray-500" data-id="element-2023">
            <ArrowLeft className="h-5 w-5" data-id="element-2024" />
          </Link>
          <div data-id="element-2025">
            <div className="flex items-center gap-3" data-id="element-2026">
              <h1 className="text-2xl font-semibold text-gray-900" data-id="element-2027">Tag:</h1>
              <span className={`inline-flex items-center gap-1.5 px-3 py-1 rounded-full text-sm font-medium ${color.bg} ${color.text} border ${color.border}`} data-id="element-2028">
                <TagIcon className="h-3.5 w-3.5" data-id="element-2029" />
                {tagName}
              </span>
            </div>
            <p className="mt-1 text-sm text-gray-500" data-id="element-2030">
              {tag?.description || 'Viewing all submissions with this tag.'}
            </p>
          </div>
        </div>
        <button className="inline-flex items-center gap-2 px-3 py-2 bg-white border border-gray-300 text-gray-700 text-sm font-medium rounded-md hover:bg-gray-50" data-id="element-2031">
          <Download className="h-4 w-4" data-id="element-2032" />
          Export
        </button>
      </div>

      <div className="bg-white border border-gray-200 rounded-lg overflow-hidden" data-id="element-2033">
        {submissions.length === 0 ? <div className="p-12 text-center" data-id="element-2034">
            <TagIcon className="mx-auto h-12 w-12 text-gray-400" data-id="element-2035" />
            <h3 className="mt-2 text-sm font-semibold text-gray-900" data-id="element-2036">
              No submissions found
            </h3>
            <p className="mt-1 text-sm text-gray-500" data-id="element-2037">
              No submissions have been tagged with "{tagName}".
            </p>
          </div> : <table className="min-w-full divide-y divide-gray-200" data-id="element-2038">
            <thead className="bg-gray-50" data-id="element-2039">
              <tr data-id="element-2040">
                <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider" data-id="element-2041">
                  Date
                </th>
                <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider" data-id="element-2042">
                  Email
                </th>
                <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider" data-id="element-2043">
                  Source Form
                </th>
                <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider" data-id="element-2044">
                  All Tags
                </th>
                <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider" data-id="element-2045">
                  Status
                </th>
              </tr>
            </thead>
            <tbody className="bg-white divide-y divide-gray-200" data-id="element-2046">
              {submissions.map(sub => <tr key={sub.id} className="hover:bg-gray-50" data-id="element-2047">
                  <td className="px-6 py-4 whitespace-nowrap text-sm text-gray-500" data-id="element-2048">
                    {sub.date}
                  </td>
                  <td className="px-6 py-4 whitespace-nowrap text-sm font-medium text-gray-900" data-id="element-2049">
                    {sub.email}
                  </td>
                  <td className="px-6 py-4 whitespace-nowrap text-sm text-gray-500" data-id="element-2050">
                    {sub.form}
                  </td>
                  <td className="px-6 py-4 whitespace-nowrap" data-id="element-2051">
                    <div className="flex flex-wrap gap-1" data-id="element-2052">
                      {sub.tags?.map(t => <Link key={t} to={`/tags/${t}`} className={`inline-flex items-center gap-1 px-2 py-0.5 rounded text-xs font-medium ${t === tagName ? 'bg-blue-100 text-blue-800' : 'bg-gray-100 text-gray-700'}`} data-id="element-2053">
                          <TagIcon className="h-3 w-3" data-id="element-2054" />
                          {t}
                        </Link>)}
                    </div>
                  </td>
                  <td className="px-6 py-4 whitespace-nowrap" data-id="element-2055">
                    <span className={`inline-flex items-center px-2 py-0.5 rounded text-xs font-medium ${sub.status === 'accepted' ? 'bg-green-100 text-green-800' : 'bg-red-100 text-red-800'}`} data-id="element-2056">
                      {sub.status}
                    </span>
                  </td>
                </tr>)}
            </tbody>
          </table>}
      </div>
    </div>;
}